import { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';

import { listProjects } from '../../api/projects';
import type { Project } from '../../api/projects';
import { fetchDevices } from '../../api/operations';
import type { DeviceListResponse } from '../../api/operations';
import {
  CONTENT_CLASSES,
  actOnStorageOperation,
  createStorageLocation,
  createStorageOperation,
  deleteStorageLocation,
  getStorageOperationDetail,
  getStoragePreferences,
  issueRetireApproval,
  listAssetRepresentations,
  listStorageAssets,
  listStorageLocations,
  putStoragePreferences,
  reconcileStorageLocation,
} from '../../api/storage';
import type {
  ContentClass,
  RetireConfirmation,
  StorageAssetChoice,
  StorageLocation,
  StorageOperation,
  StorageOperationInput,
  StorageOperationReceipt,
  StoragePreferences,
  StorageRepresentation,
} from '../../api/storage';

const CONTENT_LABELS: Record<ContentClass, string> = {
  documents: 'Documents',
  images: 'Images',
  video: 'Video',
  audio: 'Audio',
  exports: 'Exports',
};

const LOCATION_LABELS: Record<string, string> = {
  cloud_object: 'Cloud object storage',
  node_local: 'Local Node storage',
  node_nas: 'Local Node NAS',
};

type DraftPreferences = Record<ContentClass, string>;

export const COMPUTER_STORAGE_OPERATIONS_HINT = 'Copies to or between computer folders aren’t available yet.';

export function ComputerStorageOperationHint() {
  return <p className="settings-muted">{COMPUTER_STORAGE_OPERATIONS_HINT}</p>;
}

export function storageOperationDestinations(
  locations: StorageLocation[],
  operationKind: 'link' | 'import' | 'mirror' | 'move',
  sourceLocation?: StorageLocation,
): StorageLocation[] {
  return locations.filter((location) => {
    if (location.availability.state === 'unavailable' || location.kind !== 'cloud_object') return false;
    if (operationKind === 'link') return true;
    return sourceLocation?.kind === 'cloud_object';
  });
}

export function canOfferRetireForOperation(
  operation: Pick<StorageOperation, 'source_location_id' | 'destination_location_id'>,
  locations: StorageLocation[],
): boolean {
  const operationLocationIds = [operation.source_location_id, operation.destination_location_id].filter(Boolean);
  return operationLocationIds.every((locationId) => (
    locations.find((location) => location.location_id === locationId)?.kind === 'cloud_object'
  ));
}

export function storageCheckLocations(locations: StorageLocation[]): StorageLocation[] {
  return locations.filter((location) => location.kind === 'cloud_object');
}

function emptyDraft(): DraftPreferences {
  return { documents: '', images: '', video: '', audio: '', exports: '' };
}

function draftFromPreferences(
  preferences: StoragePreferences,
  projectScope: boolean,
): DraftPreferences {
  const draft = emptyDraft();
  CONTENT_CLASSES.forEach((contentClass) => {
    const preference = preferences.preferences[contentClass];
    if (!preference) return;
    if (projectScope && preference.source !== 'project') return;
    draft[contentClass] = preference.location_id || '';
  });
  return draft;
}

function AddLocalLocation({
  deviceId,
  onCreated,
}: {
  deviceId: string;
  onCreated: () => void;
}) {
  const [rootAlias, setRootAlias] = useState('');
  const [kind, setKind] = useState<'node_local' | 'node_nas'>('node_local');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function create() {
    setBusy(true);
    setError('');
    try {
      await createStorageLocation({
        kind,
        device_id: deviceId,
        root_alias: rootAlias.trim(),
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      setRootAlias('');
      setLabel('');
      onCreated();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The Local Node location could not be added.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="settings-inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        void create();
      }}
    >
      <h4>Add a Local Node location</h4>
      <p>On this paired computer, run <code>auteur-bridge folders add &lt;alias&gt; &lt;dir&gt;</code> with the directory path. Enter the same alias here. The directory stays on your computer.</p>
      <label>
        Storage type
        <select value={kind} onChange={(event) => setKind(event.target.value as 'node_local' | 'node_nas')}>
          <option value="node_local">Computer drive</option>
          <option value="node_nas">Mounted NAS</option>
        </select>
      </label>
      <label>
        Root alias
        <input
          value={rootAlias}
          maxLength={64}
          autoComplete="off"
          onChange={(event) => setRootAlias(event.target.value)}
        />
      </label>
      <label>
        Display label (optional)
        <input
          value={label}
          maxLength={128}
          onChange={(event) => setLabel(event.target.value)}
        />
      </label>
      {error ? <p className="error" role="alert">{error}</p> : null}
      <button type="submit" disabled={busy || !rootAlias.trim()}>
        {busy ? 'Adding…' : 'Add location'}
      </button>
    </form>
  );
}

function RepresentationList({
  representations,
  locations,
  onSelect,
}: {
  representations: StorageRepresentation[];
  locations: StorageLocation[];
  onSelect: (representationId: string) => void;
}) {
  const locationById = new Map<string, StorageLocation>();
  locations.forEach((location) => locationById.set(location.location_id, location));
  if (representations.length === 0) return <p>No copies were returned for this asset.</p>;
  return (
    <>
    <p>Asset ID: <code>{representations[0].asset_id}</code></p>
    <ul className="storage-representation-list">
      {representations.map((representation) => {
        const location = locationById.get(representation.location_id);
        return (
          <li className="settings-card" key={representation.representation_id}>
            <div className="settings-card-heading">
              <div>
                <strong>{location?.label || LOCATION_LABELS[representation.kind] || representation.kind}</strong>
                <p className="settings-muted">
                  {representation.availability}
                  {representation.is_primary ? ' · Primary copy' : ''}
                </p>
              </div>
              <button type="button" className="secondary" onClick={() => onSelect(representation.representation_id)}>
                Use as source
              </button>
            </div>
            <p>File: {representation.file_name}</p>
            {representation.verified_at ? (
              <p>Last verified {new Date(representation.verified_at).toLocaleString()}</p>
            ) : (
              <p>Not verified</p>
            )}
          </li>
        );
      })}
    </ul>
    </>
  );
}

export interface StoragePanelProps {
  mode?: 'settings' | 'intake';
  onDirtyChange?: (dirty: boolean) => void;
  onDefaultsConfirmed?: () => void;
}

function intakeStorageLabel(kind?: string): string {
  return kind === 'node_local' || kind === 'node_nas'
    ? 'Local Node storage on your computer'
    : 'Cloud storage';
}

export function StoragePanel({
  mode = 'settings',
  onDirtyChange,
  onDefaultsConfirmed,
}: StoragePanelProps) {
  const [locations, setLocations] = useState<StorageLocation[] | null>(null);
  const [devices, setDevices] = useState<DeviceListResponse | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [preferences, setPreferences] = useState<StoragePreferences | null>(null);
  const [draft, setDraft] = useState<DraftPreferences>(emptyDraft());
  const [baseline, setBaseline] = useState<DraftPreferences>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [assetId, setAssetId] = useState('');
  const [assetChoices, setAssetChoices] = useState<StorageAssetChoice[]>([]);
  const [assetQuery, setAssetQuery] = useState('');
  const [originKey, setOriginKey] = useState('');
  const [representations, setRepresentations] = useState<StorageRepresentation[] | null>(null);
  const [representationError, setRepresentationError] = useState('');
  const [operationKind, setOperationKind] = useState<'link' | 'import' | 'mirror' | 'move'>('link');
  const [sourceRepresentationId, setSourceRepresentationId] = useState('');
  const [destinationLocationId, setDestinationLocationId] = useState('');
  const [destinationRelativeKey, setDestinationRelativeKey] = useState('');
  const [relativeKey, setRelativeKey] = useState('');
  const [operation, setOperation] = useState<StorageOperation | null>(null);
  const [receipts, setReceipts] = useState<StorageOperationReceipt[]>([]);
  const [reconcileLocationId, setReconcileLocationId] = useState('');
  const [operationBusy, setOperationBusy] = useState(false);
  const [operationError, setOperationError] = useState('');

  const dirty = CONTENT_CLASSES.some((contentClass) => draft[contentClass] !== baseline[contentClass]);
  const projectScope = Boolean(projectId);

  const reloadLocations = useCallback(async () => {
    const result = await listStorageLocations();
    setLocations(result);
  }, []);

  useEffect(() => {
    let active = true;
    const load = mode === 'intake'
      ? listStorageLocations().then((locationRows) => [locationRows, [] as Project[]] as const)
      : Promise.all([listStorageLocations(), listProjects()]);
    load
      .then(([locationRows, projectRows]) => {
        if (!active) return;
        setLocations(locationRows);
        setProjects(projectRows);
      })
      .catch((failure: unknown) => {
        if (active) setError(failure instanceof Error ? failure.message : 'Storage settings could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, [mode]);

  useEffect(() => {
    let active = true;
    function reloadPairedNodes() {
      void fetchDevices()
        .then((result) => { if (active) setDevices(result); })
        .catch(() => { if (active) setDevices(null); });
    }
    reloadPairedNodes();
    window.addEventListener('focus', reloadPairedNodes);
    return () => { active = false; window.removeEventListener('focus', reloadPairedNodes); };
  }, []);

  useEffect(() => {
    let active = true;
    if (!projectId || mode === 'intake') {
      setAssetChoices([]);
      return () => { active = false; };
    }
    listStorageAssets(projectId, assetQuery)
      .then((result) => { if (active) setAssetChoices(result); })
      .catch(() => { if (active) setAssetChoices([]); });
    return () => { active = false; };
  }, [projectId, assetQuery, mode]);

  useEffect(() => {
    let active = true;
    setPreferences(null);
    setDraft(emptyDraft());
    setBaseline(emptyDraft());
    getStoragePreferences(projectId || undefined)
      .then((result) => {
        if (!active) return;
        const nextDraft = draftFromPreferences(result, Boolean(projectId));
        setPreferences(result);
        setDraft(nextDraft);
        setBaseline(nextDraft);
        setError('');
      })
      .catch((failure: unknown) => {
        if (active) setError(failure instanceof Error ? failure.message : 'Storage preferences could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const sourceCopy = representations?.find((copy) => copy.representation_id === sourceRepresentationId);
  const sourceLocation = locations?.find((location) => location.location_id === sourceCopy?.location_id);
  const allowedDestinations = storageOperationDestinations(locations || [], operationKind, sourceLocation);
  const hasComputerLocations = (locations || []).some((location) => location.kind !== 'cloud_object');
  const effectiveDestination = allowedDestinations.find((location) => location.location_id === destinationLocationId)?.location_id
    || allowedDestinations.find((location) => location.location_id === preferences?.preferences.documents?.location_id)?.location_id
    || allowedDestinations[0]?.location_id
    || '';

  function changeProjectScope(nextProjectId: string) {
    if (dirty && !window.confirm('Discard your unsaved storage changes?')) return;
    setProjectId(nextProjectId);
    setAssetId('');
    setAssetQuery('');
    setOriginKey('');
    setRepresentations(null);
    setSourceRepresentationId('');
    setOperation(null);
    setReceipts([]);
    setRepresentationError('');
    setOperationError('');
  }

  async function savePreferences() {
    setSaving(true);
    setError('');
    setNotice('');
    const values: Partial<Record<ContentClass, string | null>> = {};
    CONTENT_CLASSES.forEach((contentClass) => {
      if (draft[contentClass] !== baseline[contentClass]) {
        values[contentClass] = draft[contentClass] || null;
      }
    });
    try {
      const saved = await putStoragePreferences({
        ...(projectId ? { projectId } : {}),
        values,
        confirmDefaults: mode === 'intake' || !projectId,
      });
      const nextDraft = draftFromPreferences(saved, Boolean(projectId));
      setPreferences(saved);
      setDraft(nextDraft);
      setBaseline(nextDraft);
      setNotice(projectId
        ? 'Project storage overrides saved.'
        : mode === 'intake'
          ? 'Storage defaults confirmed.'
          : 'Organization storage defaults saved.');
      if (mode === 'intake') onDefaultsConfirmed?.();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Storage preferences could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  async function loadRepresentations(selectedAssetId = assetId) {
    setRepresentationError('');
    setRepresentations(null);
    setSourceRepresentationId('');
    if (!projectId || !selectedAssetId.trim()) return;
    try {
      const result = await listAssetRepresentations(projectId, selectedAssetId.trim());
      setRepresentations(result);
      setSourceRepresentationId((current) => (
        current && result.some((representation) => representation.representation_id === current)
          ? current
          : (result.find((representation) => representation.is_primary)?.representation_id || result[0]?.representation_id || '')
      ));
    } catch (failure) {
      setRepresentationError(failure instanceof Error ? failure.message : 'Asset representations could not be loaded.');
    }
  }

  async function showOperation(next: StorageOperation, scopeProjectId: string) {
    setOperation(next);
    setReceipts([]);
    try {
      const detail = await getStorageOperationDetail(scopeProjectId, next.operation_id);
      setOperation(detail.operation);
      setReceipts(detail.receipts);
    } catch {
      setOperationError('The operation was accepted, but its receipts could not be loaded. Use Refresh operation.');
    }
  }

  async function startStorageOperation() {
    if (!projectId) return;
    setOperationBusy(true);
    setOperationError('');
    setNotice('');
    const idempotencyKey = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
    let input: StorageOperationInput;
    if (operationKind === 'link') {
      input = {
        op: 'link',
        idempotency_key: idempotencyKey,
        location_id: effectiveDestination,
        relative_key: relativeKey.trim(),
        ...(assetId.trim()
          ? { asset_id: assetId.trim() }
          : (originKey.trim() ? { origin_key: originKey.trim() } : {})),
      };
    } else {
      input = {
        op: operationKind,
        idempotency_key: idempotencyKey,
        asset_id: assetId.trim(),
        source_representation_id: sourceRepresentationId || undefined,
        destination_location_id: effectiveDestination,
        ...(destinationRelativeKey.trim() ? { destination_relative_key: destinationRelativeKey.trim() } : {}),
      };
    }
    try {
      const created = await createStorageOperation(projectId, input);
      await showOperation(created.operation, projectId);
      setNotice(created.duplicate ? 'The existing operation was returned.' : 'Storage operation started.');
      if (created.operation.asset_id) {
        setAssetId(created.operation.asset_id);
        try {
          const result = await listAssetRepresentations(projectId, created.operation.asset_id);
          setRepresentations(result);
        } catch {
          setRepresentations(null);
        }
      }
      try {
        setAssetChoices(await listStorageAssets(projectId, assetQuery));
      } catch {
        // The accepted operation remains visible; the picker refresh can wait.
      }
    } catch (failure) {
      setOperationError(failure instanceof Error ? failure.message : 'The storage operation could not be started.');
    } finally {
      setOperationBusy(false);
    }
  }

  async function refreshOperation() {
    if (!projectId || !operation) return;
    setOperationBusy(true);
    setOperationError('');
    try {
      const detail = await getStorageOperationDetail(projectId, operation.operation_id);
      setOperation(detail.operation);
      setReceipts(detail.receipts);
    } catch (failure) {
      setOperationError(failure instanceof Error ? failure.message : 'The operation status could not be loaded.');
    } finally {
      setOperationBusy(false);
    }
  }

  async function confirmRetire(retireConfirmation: RetireConfirmation) {
    if (!projectId || !operation) return;
    const accepted = window.confirm(
      'The verified destination is primary. Retire the previous copy of this file? The server will check the exact file, copies, checksum and operation again.',
    );
    if (!accepted) return;
    setOperationBusy(true);
    setOperationError('');
    try {
      const approval = retireConfirmation.approval_required
        ? await issueRetireApproval(
          projectId, operation.operation_id, retireConfirmation.binding,
          'Customer confirmed retirement of the verified previous copy',
        )
        : null;
      const updated = await actOnStorageOperation(
        projectId,
        operation.operation_id,
        'confirm_retire',
        retireConfirmation.binding,
        approval?.approval_receipt_id,
      );
      setNotice('Retire confirmation sent.');
      await showOperation(updated, projectId);
    } catch (failure) {
      setOperationError(failure instanceof Error ? failure.message : 'The old copy could not be retired.');
    } finally {
      setOperationBusy(false);
    }
  }

  async function cancelOperation() {
    if (!projectId || !operation || !window.confirm('Cancel this operation? Existing copies will remain.')) return;
    setOperationBusy(true);
    setOperationError('');
    try {
      const updated = await actOnStorageOperation(projectId, operation.operation_id, 'cancel');
      await showOperation(updated, projectId);
      setNotice('Operation cancelled.');
    } catch (failure) {
      setOperationError(failure instanceof Error ? failure.message : 'The operation could not be cancelled.');
    } finally {
      setOperationBusy(false);
    }
  }

  async function startReconcile(reason: 'reconcile' | 'reroot') {
    if (!projectId || !reconcileLocationId) return;
    if (reason === 'reroot' && !window.confirm('Recheck this Local Node location after changing its local directory mapping? Copies will be pending until verified again.')) return;
    setOperationBusy(true);
    setOperationError('');
    try {
      const started = await reconcileStorageLocation(projectId, reconcileLocationId, reason);
      await showOperation(started, projectId);
      setNotice(reason === 'reroot' ? 'Re-root check started.' : 'Reconcile started.');
    } catch (failure) {
      setOperationError(failure instanceof Error ? failure.message : 'The location could not be checked.');
    } finally {
      setOperationBusy(false);
    }
  }

  async function removeLocation(location: StorageLocation) {
    if (!window.confirm('Remove storage location ' + location.label + '? Existing primary copies must be moved first. Files on the device are not deleted.')) return;
    setError('');
    try {
      const removed = await deleteStorageLocation(location.location_id);
      await reloadLocations();
      setNotice('Location removed at ' + new Date(removed.deleted_at).toLocaleString() + '. No files were deleted.');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The location could not be removed.');
    }
  }

  const canStartOperation = Boolean(
    projectId
    && effectiveDestination
    && allowedDestinations.some((location) => location.location_id === effectiveDestination)
    && (operationKind === 'link'
      ? relativeKey.trim() && (assetId.trim() || originKey.trim())
      : assetId.trim() && sourceRepresentationId
        && (sourceLocation?.location_id !== effectiveDestination || destinationRelativeKey.trim())),
  );

  return (
    <section className="settings-panel-content">
      <h3>Storage</h3>
      <p className="settings-note">
        {mode === 'intake'
          ? 'These choices apply to new files. Files already saved stay where they are.'
          : 'Changing a default affects new files only. Existing files move only through an explicit storage operation.'}
      </p>
      {error ? <p className="error" role="alert">{error}</p> : null}
      {notice ? <p className="success" role="status">{notice}</p> : null}

      <div className="storage-preferences">
        {mode === 'intake' ? (
          <p>Here are the storage choices that will apply to new projects and new files.</p>
        ) : (
          <>
            <label htmlFor="storage-project-scope">Set organization defaults or override one project</label>
            <select
              id="storage-project-scope"
              value={projectId}
              onChange={(event) => changeProjectScope(event.target.value)}
            >
              <option value="">Organization defaults</option>
              {projects.map((project) => (
                <option key={project.project_id} value={project.project_id}>
                  {project.display_name}
                </option>
              ))}
            </select>
          </>
        )}
        {preferences ? (
          <div className="storage-class-grid">
            {CONTENT_CLASSES.map((contentClass) => {
              const current = preferences.preferences[contentClass];
              const currentLocation = locations?.find((location) => location.location_id === current?.location_id);
              const placeholder = projectScope ? 'Use organization default' : 'Use derived default';
              return (
                <div className="storage-class-row" key={contentClass}>
                  <label htmlFor={'storage-' + contentClass}>{CONTENT_LABELS[contentClass]}</label>
                  <p className="settings-muted">
                    Current: {mode === 'intake'
                      ? intakeStorageLabel(current?.kind || preferences.derived_defaults[contentClass]?.kind)
                      : currentLocation?.label || LOCATION_LABELS[current?.kind || ''] || 'Derived default'}
                    {currentLocation?.label && mode === 'intake' ? ' · ' + currentLocation.label : ''}
                    {current?.availability?.state === 'unavailable' ? ' · currently unavailable' : ''}
                    {current?.availability?.state === 'unverified' ? ' · root not yet verified' : ''}
                  </p>
                  <select
                    id={'storage-' + contentClass}
                    value={draft[contentClass]}
                    onChange={(event) => setDraft((currentDraft) => ({
                      ...currentDraft,
                      [contentClass]: event.target.value,
                    }))}
                  >
                    <option value="">{placeholder}</option>
                    {(locations || []).filter((location) => location.availability.state !== 'unavailable').map((location) => (
                      <option key={location.location_id} value={location.location_id}>
                        {location.label} — {LOCATION_LABELS[location.kind] || location.kind}
                        {location.availability.state === 'unverified' ? ' (Root not yet verified)' : ''}
                      </option>
                    ))}
                  </select>
                  {preferences.derived_defaults[contentClass]?.requires_location ? (
                    <p className="notice">
                      This Local Node default needs a storage location. You can configure one in{' '}
                      <Link to="/settings?settings=storage">Settings</Link> or <Link to="/devices/pair">pair a Local Node</Link>.
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p role="status">Loading storage preferences…</p>
        )}
        <div className="settings-actions">
          <button
            type="button"
            disabled={(!dirty && mode !== 'intake') || saving || !preferences}
            onClick={() => void savePreferences()}
          >
            {saving
              ? 'Saving…'
              : mode === 'intake'
                ? 'Confirm defaults and continue'
                : 'Save storage preferences'}
          </button>
          {dirty && mode !== 'intake' ? (
            <button type="button" className="secondary" onClick={() => setDraft(baseline)}>
              Discard changes
            </button>
          ) : null}
        </div>
      </div>

      <section className="settings-subsection">
        <h4>Storage locations</h4>
        {locations === null ? (
          <p role="status">Loading locations…</p>
        ) : locations.length === 0 ? (
          <p>No storage locations were returned.</p>
        ) : (
          <ul className="storage-location-list">
            {locations.map((location) => (
              <li className="settings-card" key={location.location_id}>
                <strong>{location.label}</strong>
                <span> · {LOCATION_LABELS[location.kind] || location.kind}</span>
                <p className={location.availability.state === 'available' ? 'success' : 'settings-muted'}>
                  {location.availability.state === 'available' ? 'Available'
                    : location.availability.state === 'unverified' ? 'Root not yet verified on this computer'
                      : 'Unavailable' + (location.availability.reason ? ' · ' + location.availability.reason.replace(/_/g, ' ') : '')}
                </p>
                {!location.built_in ? (
                  <button type="button" className="danger-button" onClick={() => void removeLocation(location)}>
                    Remove location
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {(devices?.devices || []).filter((device) => device.status !== 'revoked').map((device) => (
          <AddLocalLocation
            key={device.device_id}
            deviceId={device.device_id}
            onCreated={() => {
              void reloadLocations().catch((failure: unknown) => {
                setError(failure instanceof Error ? failure.message : 'Storage locations could not be refreshed.');
              });
            }}
          />
        ))}
        {!devices?.devices?.some((device) => device.status !== 'revoked') ? (
          mode === 'intake'
            // Welcome does not send anyone to the pairing page mid-setup (F-14): local folders need the
            // desktop app, which is not available yet.
            ? <p>Folders on your own computer: coming with the Auteur desktop app. Cloud storage is used until then.</p>
            : <p>Pair a <Link to="/devices/pair">Local Node</Link> to add computer or NAS storage.</p>
        ) : null}
      </section>

      {mode !== 'intake' ? (
      <section className="settings-subsection">
        <h4>Files and copies</h4>
        <p>Choose a file in this project to see its copies, or link a new file already stored at a location.</p>
        <label htmlFor="storage-asset-project">Project</label>
        <select
          id="storage-asset-project"
          value={projectId}
          onChange={(event) => changeProjectScope(event.target.value)}
        >
          <option value="">Choose a project</option>
          {projects.map((project) => (
            <option key={project.project_id} value={project.project_id}>{project.display_name}</option>
          ))}
        </select>
        <label htmlFor="storage-asset-search">Find a file by name</label>
        <input id="storage-asset-search" value={assetQuery} onChange={(event) => setAssetQuery(event.target.value)} />
        <label htmlFor="storage-asset-choice">Existing file</label>
        <select
          id="storage-asset-choice"
          value={assetChoices.some((choice) => choice.asset_id === assetId) ? assetId : ''}
          onChange={(event) => {
            setAssetId(event.target.value);
            setOperation(null);
            void loadRepresentations(event.target.value);
          }}
        >
          <option value="">Choose a file</option>
          {assetChoices.map((choice) => (
            <option key={choice.asset_id} value={choice.asset_id}>
              {choice.file_name} ({choice.copy_count} {choice.copy_count === 1 ? 'copy' : 'copies'})
            </option>
          ))}
        </select>
        <details>
          <summary>Link a new file or enter an asset ID</summary>
          <p>Use the existing canonical asset ID when you already know it. A new file needs an origin key that identifies where it came from, such as an import reference; it must not be a computer path.</p>
          <label htmlFor="storage-asset-id">Asset ID (optional for a new Link)</label>
          <input
            id="storage-asset-id"
            value={assetId}
            autoComplete="off"
            onChange={(event) => {
              setAssetId(event.target.value);
              setRepresentations(null);
              setOperation(null);
            }}
          />
          <label htmlFor="storage-origin-key">Where this new file came from (origin key)</label>
          <input
            id="storage-origin-key"
            value={originKey}
            autoComplete="off"
            onChange={(event) => setOriginKey(event.target.value)}
          />
        </details>
        <button
          type="button"
          className="secondary"
          disabled={!projectId || !assetId.trim()}
          onClick={() => void loadRepresentations()}
        >
          Show copies
        </button>
        {representationError ? <p className="error" role="alert">{representationError}</p> : null}
        {representations ? (
          <RepresentationList
            representations={representations}
            locations={locations || []}
            onSelect={setSourceRepresentationId}
          />
        ) : null}

        <div className="storage-operation-form">
          <label htmlFor="storage-operation-kind">Action</label>
          <select
            id="storage-operation-kind"
            value={operationKind}
            onChange={(event) => setOperationKind(event.target.value as 'link' | 'import' | 'mirror' | 'move')}
          >
            <option value="link">Link</option>
            <option value="import">Import</option>
            <option value="mirror">Mirror</option>
            <option value="move">Move</option>
          </select>
          {operationKind === 'link' ? (
            <>
              <label htmlFor="storage-relative-key">File within this location</label>
              <input
                id="storage-relative-key"
                value={relativeKey}
                autoComplete="off"
                onChange={(event) => setRelativeKey(event.target.value)}
              />
            </>
          ) : (
            <>
              <label htmlFor="storage-source-representation">Copy to use as source</label>
              <select
                id="storage-source-representation"
                value={sourceRepresentationId}
                onChange={(event) => setSourceRepresentationId(event.target.value)}
              >
                <option value="">Choose a copy</option>
                {(representations || []).map((representation) => (
                  <option key={representation.representation_id} value={representation.representation_id}>
                    {representation.file_name}{representation.is_primary ? ' — primary' : ''}
                  </option>
                ))}
              </select>
              <label htmlFor="storage-destination-file">File name within destination</label>
              <input
                id="storage-destination-file"
                value={destinationRelativeKey}
                autoComplete="off"
                placeholder="Required when using the same location"
                onChange={(event) => setDestinationRelativeKey(event.target.value)}
              />
            </>
          )}
          <label htmlFor="storage-destination-location">Destination location</label>
          <select
            id="storage-destination-location"
            value={effectiveDestination}
            onChange={(event) => setDestinationLocationId(event.target.value)}
          >
            <option value="">Choose a location</option>
            {allowedDestinations.map((location) => (
              <option key={location.location_id} value={location.location_id}>
                {location.label} ({location.availability.state === 'unverified' ? 'root unverified' : location.availability.state})
              </option>
            ))}
          </select>
          {operationKind !== 'link' ? (
            <ComputerStorageOperationHint />
          ) : null}
          {operationKind === 'link' && hasComputerLocations ? (
            <p className="settings-muted">Links to computer folders aren&apos;t available yet.</p>
          ) : null}
          <button
            type="button"
            disabled={!canStartOperation || operationBusy}
            onClick={() => void startStorageOperation()}
          >
            {operationBusy ? 'Working…' : operationKind}
          </button>
        </div>

        <div className="storage-operation-form">
          <h5>Check a location</h5>
          <p>Reconcile checks cloud copies against files.</p>
          {hasComputerLocations ? <p className="settings-muted">Reconcile, re-root, and retirement for computer folders aren&apos;t available yet.</p> : null}
          <label htmlFor="storage-reconcile-location">Location to check</label>
          <select
            id="storage-reconcile-location"
            value={reconcileLocationId}
            onChange={(event) => setReconcileLocationId(event.target.value)}
          >
            <option value="">Choose a location</option>
            {storageCheckLocations(locations || []).map((location) => (
              <option key={location.location_id} value={location.location_id}>{location.label}</option>
            ))}
          </select>
          <div className="settings-actions">
            <button type="button" className="secondary" disabled={!projectId || !reconcileLocationId || operationBusy}
              onClick={() => void startReconcile('reconcile')}>Reconcile copies</button>
          </div>
        </div>

        {operationError ? <p className="error" role="alert">{operationError}</p> : null}
        {operation ? (
          <div className="settings-card storage-operation-result" aria-live="polite">
            <h5>{operation.op} operation</h5>
            {operation.asset_id ? <p>Asset ID: <code>{operation.asset_id}</code></p> : null}
            <p>State: <strong>{operation.state}</strong></p>
            {operation.failure_code ? <p>Reason: {operation.failure_code.replace(/_/g, ' ')}</p> : null}
            {operation.awaiting === 'node_result' ? (
              <p>The Local Node has not returned this operation result yet. Refresh to check again.</p>
            ) : null}
            <button type="button" className="secondary" disabled={operationBusy} onClick={() => void refreshOperation()}>
              Refresh operation
            </button>
            {!['retired', 'reconciled', 'failed', 'cancelled'].includes(operation.state) ? (
              <button type="button" className="secondary" disabled={operationBusy} onClick={() => void cancelOperation()}>
                Cancel operation
              </button>
            ) : null}
            {operation.awaiting === 'confirm_retire' && operation.retire_confirmation
              && canOfferRetireForOperation(operation, locations || []) ? (
              <div className="notice">
                <p>The destination is verified and primary. Confirm before retiring the previous copy.
                  {operation.retire_confirmation.approval_required ? ' A short-lived approval for this exact move will be recorded.' : ''}
                </p>
                <button
                  type="button"
                  disabled={operationBusy}
                  onClick={() => void confirmRetire(operation.retire_confirmation as RetireConfirmation)}
                >
                  Confirm retire old copy
                </button>
              </div>
            ) : null}
            {receipts.length ? (
              <div>
                <h6>Operation receipts</h6>
                <ol>
                  {receipts.map((receipt) => (
                    <li key={receipt.receipt_id}>
                      {receipt.step.replace(/_/g, ' ')} → {receipt.to_state.replace(/_/g, ' ')}
                      {receipt.recorded_at ? ' · ' + new Date(receipt.recorded_at).toLocaleString() : ''}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
      ) : null}
    </section>
  );
}
